import { trackEvent } from '@lib/analytics'
import React from 'react'
import {
	Actions,
	Container,
	Details,
	Message,
	RetryButton,
	SecondaryButton,
	Title,
} from './ErrorBoundary.styles'

/**
 * Catches render-time errors in the subtree and shows a fallback instead of
 * letting React unmount the whole app.
 *
 * React tears the entire tree down when a render throws, so a single bad row
 * — an expense whose split violates the wire contract, say — would otherwise
 * blank the page. Mount this around page content, not around the navigation,
 * so a broken page still leaves the user somewhere to go.
 *
 * Boundaries latch: once caught, the fallback stays until state is cleared.
 * Callers that want a fresh boundary per route should pass a changing `key`.
 */
interface ErrorBoundaryProps {
	children: React.ReactNode
	/** Replaces the default fallback. Receives a reset callback for a retry affordance. */
	fallback?: (error: Error, reset: () => void) => React.ReactNode
}

interface ErrorBoundaryState {
	error: Error | null
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	state: ErrorBoundaryState = { error: null }

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { error }
	}

	override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
		// A white screen nobody hears about is the worst outcome — report it on
		// the analytics pipeline that is already in place.
		console.error('Unhandled render error:', error, errorInfo.componentStack)
		trackEvent('client_error', {
			message: error.message,
			name: error.name,
			path: window.location.pathname,
			componentStack: errorInfo.componentStack?.slice(0, 2000),
		})
	}

	private reset = (): void => {
		this.setState({ error: null })
	}

	override render(): React.ReactNode {
		const { error } = this.state
		if (error == null) {
			return this.props.children
		}

		if (this.props.fallback) {
			return this.props.fallback(error, this.reset)
		}

		return (
			<Container role="alert">
				<Title>Something went wrong</Title>
				<Message>
					This page couldn&apos;t be displayed. The rest of the app is still working — try again, or
					head back and come at it from another angle.
				</Message>
				<Actions>
					<RetryButton type="button" onClick={this.reset}>
						Try again
					</RetryButton>
					<SecondaryButton type="button" onClick={() => window.history.back()}>
						Go back
					</SecondaryButton>
				</Actions>
				{import.meta.env.DEV && <Details>{error.message}</Details>}
			</Container>
		)
	}
}

export default ErrorBoundary
